import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LegalFooter } from "../components/LegalFooter";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function Signup() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accept, setAccept] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (!accept) { toast.error("Please accept the Terms & Conditions and Privacy Policy."); return; }
    setLoading(true);
    const res = await register(email.trim().toLowerCase(), password, name.trim(), window.location.origin, accept);
    setLoading(false);
    if (res.ok) { toast.success("Welcome to Molaroo — your 14-day free trial has started"); navigate("/", { replace: true }); }
    else toast.error(res.error);
  };

  return (
    <div className="df-shell df-safe-top min-h-screen flex flex-col justify-center px-6">
      <div className="df-fade-in">
        <div className="flex items-center gap-3 mb-8">
          <img src="/molaroo-mark.png" alt="Molaroo" className="h-14 w-14 rounded-2xl object-contain bg-white shadow-sm" />
          <div>
            <div className="text-2xl font-heading font-bold tracking-tight text-slate-900 leading-none">Molaroo</div>
            <div className="text-sm text-slate-500 mt-1">Your income, organised.</div>
            <div className="text-xs text-slate-400 mt-0.5">by NavScora</div>
          </div>
        </div>

        <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-1">Create your account</h1>
        <p className="text-slate-500 mb-8">Start your 14-day free trial. No card needed to begin.</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="name" className="text-slate-700">Name <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input id="name" data-testid="signup-name" type="text" value={name}
              onChange={(e) => setName(e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" />
          </div>
          <div>
            <Label htmlFor="email" className="text-slate-700">Email</Label>
            <Input id="email" data-testid="signup-email" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" required />
          </div>
          <div>
            <Label htmlFor="password" className="text-slate-700">Password</Label>
            <Input id="password" data-testid="signup-password" type="password" value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters"
              className="mt-1.5 h-12 rounded-xl bg-white" required />
          </div>
          <Button type="submit" data-testid="signup-submit" disabled={loading}
            className="w-full h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] text-base font-semibold df-pressable">
            {loading ? "Creating account…" : "Create account & start free trial"}
          </Button>
          <label className="flex items-start gap-2.5 text-xs text-slate-500 mt-1 cursor-pointer">
            <input type="checkbox" data-testid="signup-accept" checked={accept}
              onChange={(e) => setAccept(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#0D786A]" />
            <span>By creating an account, you agree to the Molaroo{" "}
              <Link to="/terms" data-testid="signup-terms-link" className="text-[#0D786A] font-medium">Terms &amp; Conditions</Link>{" "}
              and acknowledge the Molaroo{" "}
              <Link to="/privacy" data-testid="signup-privacy-link" className="text-[#0D786A] font-medium">Privacy Policy</Link>.</span>
          </label>
        </form>

        <p className="text-center text-sm text-slate-500 mt-6">
          Already have an account?{" "}
          <Link to="/login" data-testid="signup-to-login" className="text-[#0D786A] font-semibold df-pressable">Sign in</Link>
        </p>

        <div className="mt-6 flex items-start gap-2 text-xs text-slate-500 bg-white rounded-2xl p-4 border border-slate-200/70">
          <ShieldCheck size={16} className="text-[#0D786A] mt-0.5 shrink-0" />
          <span>Molaroo organises your financial records — it is not accounting, tax or legal advice.</span>
        </div>
        <LegalFooter className="mt-6" />
      </div>
    </div>
  );
}
